// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import moment, { Moment } from 'moment-timezone';
import { Event } from '@microsoft/microsoft-graph-types';
import {
  AuthProviderCallback,
  Client,
  GraphRequestOptions,
  PageCollection,
  PageIterator
} from '@microsoft/microsoft-graph-client';

export const graphServiceUtils = {
  getAuthenticatedClient: (accessToken: string): Client => {
    // Initialize Graph client
    const msGraphClient = Client.init({
      // Use the provided access token to authenticate
      // requests
      authProvider: (done: AuthProviderCallback) => {
        // In dev environment the authToken is not fetched as auth is disabled and graph client fails without a token.
        done(null, accessToken ? accessToken : 'dummyToken');
      }
    });

    return msGraphClient;
  },

  /* eslint-disable  @typescript-eslint/no-non-null-assertion */
  getCalendarEventsForToday: async (accessToken: string): Promise<Event[]> => {
    const ianaTimeZone = moment.tz.guess();
    // Get midnight on the start of the current day in the user's timezone,
    // but in UTC. For example, for Pacific Standard Time, the time value would be
    // 07:00:00Z
    const startOfDay = moment.tz(ianaTimeZone!.valueOf()).startOf('day').utc();

    // Get the user's events
    const userEvents = await graphServiceUtils.getUserEvents(accessToken, ianaTimeZone, startOfDay);
    return userEvents;
  },

  getUserEvents: async (accessToken: string, timeZone: string, startDate: Moment): Promise<Event[]> => {
    const msGraphClient = graphServiceUtils.getAuthenticatedClient(accessToken);

    const startDateTime = startDate.format();
    const endDateTime = moment(startDate).add(1, 'day').format();

    // GET /me/calendarview?startDateTime=''&endDateTime=''
    // &$select=subject,organizer,start,end
    // &$orderby=start/dateTime
    // &$top=50
    const response: PageCollection = await msGraphClient
      .api('/me/calendarview')
      .header('Prefer', `outlook.timezone="${timeZone}"`)
      .query({ startDateTime: startDateTime, endDateTime: endDateTime })
      .select('subject,organizer,start,end,body,bodyPreview,onlineMeeting,isCancelled')
      .orderby('start/dateTime')
      .top(50)
      .get();

    if (response['@odata.nextLink']) {
      // Presence of the nextLink property indicates more results are available
      // Use a page iterator to get all results
      const events: Event[] = [];
      // Must include the time zone header in page
      // requests too
      const options: GraphRequestOptions = {
        headers: { Prefer: `outlook.timezone="${timeZone}"` }
      };

      const pageIterator = new PageIterator(
        msGraphClient,
        response,
        (event) => {
          events.push(event);
          return true;
        },
        options
      );

      await pageIterator.iterate();

      return events;
    } else {
      return response.value;
    }
  }
};

const fetchProfileDisplayName = async (accessToken: string, userId: string): Promise<string | undefined> => {
  const msGraphClient = graphServiceUtils.getAuthenticatedClient(accessToken);
  try {
    return await msGraphClient.api(`/users/${userId}/displayName/$value`).get();
  } catch (e) {
    console.warn(`No display name found for user ${userId}.`, e);
    return undefined;
  }
};

const fetchProfileImage = async (accessToken: string, userId: string): Promise<Blob | undefined> => {
  const msGraphClient = graphServiceUtils.getAuthenticatedClient(accessToken);
  try {
    return await msGraphClient.api(`/users/${userId}/photo/$value`).get();
  } catch (e) {
    console.warn(`No image found for user ${userId}.`, e);
    return undefined;
  }
};

const imageBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.abort = reject;
    reader.readAsDataURL(blob);
  });
};

const CACHED_PROFILE_DATA: {
  [key: string]: {
    displayName?: string;
    profileHasNoImage?: boolean;
    base64ImageData?: string;
  };
} = {};

export const fetchProfileDisplayNameWithCaching = async (
  userId: string,
  options: {
    token: string;
  }
): Promise<string | undefined> => {
  const cachedUserData = CACHED_PROFILE_DATA[userId];
  if (cachedUserData && cachedUserData.displayName) {
    return cachedUserData.displayName;
  }

  const displayName = await fetchProfileDisplayName(options.token, userId);
  CACHED_PROFILE_DATA[userId] = {
    displayName
  };
  return displayName;
};

/**
 * Get profile image from microsoft graph.
 *
 * @remarks
 * Caching policy: the result is cached and subsequent calls will return the cached result.
 * Caching expiration: the cache is never expired.
 *
 * @returns base64 encoded image
 */
export const fetchProfileImageWithCaching = async (
  userId: string,
  options: {
    token: string;
  }
): Promise<string | undefined> => {
  const cachedUserData = CACHED_PROFILE_DATA[userId];
  if (cachedUserData) {
    return cachedUserData.base64ImageData;
  }

  const blob = await fetchProfileImage(options.token, userId);
  if (!blob) {
    CACHED_PROFILE_DATA[userId] = {
      profileHasNoImage: true
    };
    return undefined;
  }

  const base64 = await imageBlobToBase64(blob);
  CACHED_PROFILE_DATA[userId] = {
    profileHasNoImage: false,
    base64ImageData: base64
  };
  return base64;
};
